<script setup lang="ts">
    import { computed, onMounted, watch } from 'vue'
    import { useStorage } from '@vueuse/core'
    import { SelectFieldSingle } from '@/js/components/Common'
    import { useKennwerteAccess } from '@/js/components/Common/useKennwerteAccess'
    import { Fachbereich } from '@/js/model'
    import { useFachbereichStore, useStudiengangStore } from '@/js/stores'

    const emit = defineEmits(['fachbereichSelected'])
    const fachbereichStore = useFachbereichStore()
    const studiengangStore = useStudiengangStore()
    const { seesAllKennwerte, allowedFachbereichIds, allowedStudiengangIds } = useKennwerteAccess()
    const fachbereiche = computed(() => fachbereichStore.fachbereiche)

    // Aus dem Scope abgeleitete erlaubte Fachbereich-IRIs:
    // direkte FB-IDs (Studiendekan) plus die FBs der zugewiesenen Studiengänge (SFB / QMB).
    const allowedFachbereichIris = computed<Set<string> | null>(() => {
        if (seesAllKennwerte.value) {
            return null
        }
        const iris = new Set<string>()
        for (const fbId of allowedFachbereichIds.value) {
            const fb = fachbereiche.value.find((f: Fachbereich) => f.fachbereichId === fbId)
            if (fb && fb['@id']) {
                iris.add(fb['@id'])
            }
        }
        for (const sgId of allowedStudiengangIds.value) {
            const sg = studiengangStore.studiengaenge.find((s) => s.studiengangId === sgId)
            if (sg && sg.fachbereich) {
                iris.add(sg.fachbereich)
            }
        }
        return iris
    })

    const fachbereichSelectOptions = computed(() => [
        { value: 'default', title: 'Fachbereich wählen ...', disabled: true },
        ...[...fachbereiche.value]
            .filter(
                (fachbereich) =>
                    allowedFachbereichIris.value === null ||
                    allowedFachbereichIris.value.has(fachbereich['@id'])
            )
            .sort((a: Fachbereich, b: Fachbereich) => {
                const aId = a.hsaId ?? 0
                const bId = b.hsaId ?? 0

                if (aId < bId) return -1
                if (aId > bId) return 1
                return 0
            })
            .map((fachbereich) => {
                return {
                    title: `FB${fachbereich.hsaId} - ${fachbereich.kurzName}`,
                    value: fachbereich['@id']
                }
            })
    ])

    const selectedFachbereichIri = useStorage(
        'lernleitstand-fachbereich-select-on-dashboard',
        () =>
            fachbereichSelectOptions.value.length > 0
                ? fachbereichSelectOptions.value[0].value
                : 'default'
    )

    const selectedFachbereich = computed((): Fachbereich | null => {
        if (selectedFachbereichIri.value) {
            return fachbereichStore.getFachbereichByIri(selectedFachbereichIri.value) ?? null
        }

        return null
    })

    watch(
        () => selectedFachbereichIri.value,
        () => {
            emit('fachbereichSelected', selectedFachbereich.value)
        }
    )

    onMounted(() => {
        emit('fachbereichSelected', selectedFachbereich.value)
    })
</script>

<template>
    <div class="d-flex align-center">
        <select-field-single
            v-model="selectedFachbereichIri"
            :items="fachbereichSelectOptions"
            label="Fachbereich"
            placeholder="Fachbereich wählen"
            data-test="select-fachbereich"
            min-width="300px"
        />
    </div>
</template>

<style scoped></style>
